<script setup lang="ts">
import { PV } from "@/widgets";
import { watchEffect } from "vue";
import { defineActions, defineState } from "../common";
import { Buttons } from "../icons";
import { FormExt, FormGuide, FormHead, FormMessage } from "../internal";
import { type BaseProps, defaultProps } from "../props";

type ModelType = string;
interface PropsType extends BaseProps<ModelType> {
    formId?: string;
    placeholder?: string;
    rows?: number;
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
    rows: 3,
});

const model = defineModel<ModelType | undefined>({
    default: undefined,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: () => model.value !== undefined,
});

const actions = defineActions(model, props, state);

watchEffect(() => {
    if (model.value === "" || model.value === null) {
        model.value = undefined;
    }
});
</script>

<template>
    <div class="flex flex-col gap-1">
        <FormHead :actions="actions" :param="props" :state="state" />

        <template v-if="!state.editMode">
            <PV.IconField>
                <PV.Textarea v-model="model" autoResize class="rns-input-static" disabled fluid :rows="props.rows" />
                <FormExt :actions="actions" :state="state" />
            </PV.IconField>
        </template>
        <template v-else>
            <PV.IconField>
                <PV.Textarea
                    v-model.trim="model"
                    autoResize
                    :disabled="props.disabled"
                    fluid
                    :form="props.formId"
                    :inputId="state.id"
                    :invalid="state.hasError"
                    :placeholder="props.placeholder ?? `Masukkan ${label}`"
                    :required="props.required"
                    :rows="props.rows"
                    @change="actions.onValidate"
                />
                <Buttons.ExtReset v-if="state.clearable && state.hasValue" @click="actions.onReset" />
            </PV.IconField>
        </template>

        <FormGuide :state="state" :text="props.guideText" />
        <FormMessage :state="state" />
    </div>
</template>

<style scoped>
@reference "@/styles/main.css";

.rns-input-static {
    @apply text-gray-700! bg-transparent! shadow-none! px-2! pt-2.25!;
    @apply border rounded-none border-gray-400;
}
</style>
